import React, { memo, useCallback } from "react";
import { SecurityIssue } from "../../types/security";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { getSeverityColor, formatRiskScore, getIssueStatusColor } from "@/utils/securityHelpers";

interface SecurityIssuesListProps {
  issues: SecurityIssue[];
  onIssueClick: (issue: SecurityIssue) => void;
}

interface SecurityIssueRowProps {
  issue: SecurityIssue;
  onIssueClick: (issue: SecurityIssue) => void;
}

const SecurityIssueRow = memo(
  function SecurityIssueRow({ issue, onIssueClick }: SecurityIssueRowProps) {
    const handleClick = useCallback(() => {
      onIssueClick(issue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issue.id, onIssueClick]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onIssueClick(issue);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [issue.id, onIssueClick]
    );

    // Clamp riskScore to valid 0-1 range for CSS width calculation
    const clampedRiskScore = Math.max(0, Math.min(1, issue.riskScore));
    const riskScorePercentage = clampedRiskScore * 100;

    return (
      <TableRow
        role="button"
        tabIndex={0}
        aria-label={`View details for ${issue.title}`}
        className="cursor-pointer hover:bg-muted/50 focus:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <TableCell className="font-medium text-xs max-w-md truncate">
          {issue.title}
        </TableCell>
        <TableCell>
          <Badge className={`${getSeverityColor(issue.severity)} text-xs`}>
            {issue.severity}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge
            variant="outline"
            className={`${getIssueStatusColor(issue.status)} text-xs`}
          >
            {issue.status.replace(/_/g, " ")}
          </Badge>
        </TableCell>
        <TableCell className="text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${
                  clampedRiskScore >= 0.8
                    ? "bg-red-600"
                    : clampedRiskScore >= 0.6
                      ? "bg-orange-500"
                      : clampedRiskScore >= 0.4
                        ? "bg-yellow-500"
                        : "bg-green-500"
                }`}
                style={{ width: `${riskScorePercentage}%` }}
              />
            </div>
            <span className="font-medium">{formatRiskScore(clampedRiskScore)}</span>
          </div>
        </TableCell>
      </TableRow>
    );
  },
  // Custom comparison: only re-render if id changes or callback reference changes
  // This prevents re-renders when only the issue object reference changes
  // but the actual data is the same
  (prevProps, nextProps) =>
    prevProps.issue.id === nextProps.issue.id &&
    prevProps.issue.title === nextProps.issue.title &&
    prevProps.issue.severity === nextProps.issue.severity &&
    prevProps.issue.status === nextProps.issue.status &&
    prevProps.issue.riskScore === nextProps.issue.riskScore &&
    prevProps.onIssueClick === nextProps.onIssueClick
);

export const SecurityIssuesList: React.FC<SecurityIssuesListProps> = memo(function SecurityIssuesList({ issues, onIssueClick }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Title</TableHead>
            <TableHead className="text-xs">Severity</TableHead>
            <TableHead className="text-xs">Status</TableHead>
            <TableHead className="text-xs">Risk Score</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {issues.map((issue) => (
            <SecurityIssueRow key={issue.id} issue={issue} onIssueClick={onIssueClick} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
});
